import { HERO_SCREENSHOT } from '@/content/screenshots';

/**
 * Valanti Labs phone mockup.
 *
 * If a real product screenshot is provided via `screenshot.src`, the device frame displays
 * that image as-is. Otherwise it renders an abstract illustration of the in-app experience.
 *
 * NOTE: any percentages, accuracy figures or privacy statements that appear inside a real
 * screenshot are unverified interface content. The surrounding public copy must not repeat
 * them as confirmed claims.
 */
export default function PhoneMockup({ className = '', variant = 'default', screenshot = HERO_SCREENSHOT }) {
  const hasScreenshot = Boolean(screenshot && screenshot.src);

  return (
    <figure className={`relative ${className}`}>
      <div
        className="relative mx-auto rounded-[44px] border border-vl-ink/15 bg-vl-bg2 shadow-[0_30px_80px_-30px_rgba(26,26,58,0.35)] overflow-hidden"
        style={{ width: '280px', height: '580px' }}
        aria-hidden={hasScreenshot ? undefined : true}
      >
        <div className="absolute top-3 left-1/2 -translate-x-1/2 h-5 w-24 bg-vl-ink/85 rounded-full z-10" />
        <div className="absolute inset-2 rounded-[36px] bg-vl-surface overflow-hidden">
          {hasScreenshot ? (
            <img
              src={screenshot.src}
              alt={screenshot.alt || 'Valanti Labs application screen.'}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <AbstractScreen />
          )}
        </div>
      </div>

      {variant === 'default' && (
        <div className="hidden md:block absolute -right-10 top-24 max-w-[200px] rounded-2xl bg-vl-surface border hairline px-4 py-3 shadow-md">
          <p className="mono-label text-vl-ink/55">Reliability</p>
          <p className="text-sm text-vl-ink mt-1 leading-snug">
            The system tells you when it isn't confident — instead of guessing.
          </p>
        </div>
      )}

      <figcaption className="sr-only">
        Illustrative representation of the Valanti Labs application. Final interface is subject to release-version
        configuration.
      </figcaption>
    </figure>
  );
}

function AbstractScreen() {
  return (
    <div className="w-full h-full flex flex-col" aria-hidden="true">
      <div className="px-5 pt-7 pb-2 flex items-center justify-between font-mono text-[10px] text-vl-ink/70">
        <span>9:41</span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-1.5 rounded-sm bg-vl-ink/60" />
          <span className="inline-block w-1 h-1.5 rounded-sm bg-vl-ink/60" />
          <span className="inline-block w-2 h-1.5 rounded-sm bg-vl-ink/60" />
        </span>
      </div>

      <div className="px-5 pt-3">
        <p className="mono-label text-vl-ink/50">Supported movement</p>
        <h4 className="font-serif-display text-[22px] text-vl-ink leading-tight mt-1">
          Movement analysis preview
        </h4>
        <p className="text-[11px] text-vl-ink/55 mt-1">Illustrative — release configuration may differ</p>
      </div>

      <div className="mx-4 mt-4 rounded-2xl bg-gradient-to-br from-[#F0EFEA] to-[#E5E4E0] flex-1 relative overflow-hidden">
        <svg viewBox="0 0 200 240" className="absolute inset-0 w-full h-full">
          <defs>
            <radialGradient id="silh" cx="50%" cy="40%" r="60%">
              <stop offset="0%" stopColor="#1A1A3A" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#1A1A3A" stopOpacity="0" />
            </radialGradient>
          </defs>
          <ellipse cx="100" cy="100" rx="90" ry="120" fill="url(#silh)" />
          <g stroke="#6B6699" strokeWidth="1.3" fill="none" className="vl-pulse">
            <circle cx="100" cy="62" r="8" fill="#6B6699" fillOpacity="0.25" />
            <line x1="100" y1="70" x2="100" y2="135" />
            <line x1="80" y1="80" x2="120" y2="80" />
            <line x1="80" y1="80" x2="64" y2="120" />
            <line x1="120" y1="80" x2="140" y2="115" />
            <line x1="86" y1="135" x2="114" y2="135" />
            <line x1="86" y1="135" x2="78" y2="180" />
            <line x1="78" y1="180" x2="70" y2="215" />
            <line x1="114" y1="135" x2="150" y2="160" />
            <line x1="150" y1="160" x2="178" y2="172" />
            {[
              [80, 80], [120, 80], [64, 120], [140, 115],
              [86, 135], [114, 135], [78, 180], [70, 215],
              [150, 160], [178, 172],
            ].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2.4" fill="#6B6699" />
            ))}
          </g>
          <path d="M114 135 A 35 35 0 0 1 150 160" stroke="#2B5B84" strokeWidth="1.2" fill="none" strokeDasharray="3 3" />
          <text x="148" y="140" fontSize="8" fill="#2B5B84" fontFamily="IBM Plex Mono">ROM</text>
        </svg>
        <div className="absolute top-3 left-3 mono-label bg-vl-surface/90 text-vl-ink/70 px-2 py-1 rounded-full">
          ILLUSTRATIVE
        </div>
        <div className="absolute top-3 right-3 mono-label bg-vl-surface/90 text-vl-ink/70 px-2 py-1 rounded-full">
          SIDE VIEW
        </div>
      </div>

      <div className="px-5 py-4 space-y-2.5">
        <ReliabilityRow label="Posture" tone="high" pct={78} stateLabel="RELIABLE" />
        <ReliabilityRow label="Range of motion" tone="med" pct={46} stateLabel="CHECKING" />
        <ReliabilityRow label="Tempo consistency" tone="violet" pct={32} stateLabel="CALCULATING" />
      </div>
    </div>
  );
}

function ReliabilityRow({ label, tone, pct, stateLabel }) {
  const colour = tone === 'high' ? '#4C7C59' : tone === 'med' ? '#D4A373' : '#6B6699';
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="mono-label text-vl-ink/55">{label}</span>
        <span className="mono-label" style={{ color: colour }}>{stateLabel}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-vl-grey overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: colour }} />
      </div>
    </div>
  );
}
